import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const CommunicationTemplateCategoryContext = createContext();
 
export const useCommunicationTemplateCategories = () => {
  const context = useContext(CommunicationTemplateCategoryContext);
  if (!context) {
    throw new Error('useCommunicationTemplateCategories must be used within a CommunicationTemplateCategoryProvider');
  }
  return context;
};

export const CommunicationTemplateCategoryProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [communicationTemplateCategories, setCommunicationTemplateCategories] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/communication-template-categories');

  const getCommunicationTemplateCategories = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      setPaginate(true)
      let response = await apiInstance.call('communication-template-categories?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          if(filters?.active === true){
            query += 'active=1';
          }else{
            query += 'active=0';
          }
         
        }
        response = await apiInstance.call('communication-template-categories?'+query);  
      }
      
      if (response.status === 200) {
       // setPaginate(response.data.paginate)
        setCommunicationTemplateCategories(response.data.cTemplateCategories || []);
       
        
        if(filters){
          return response.data.cTemplateCategories;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch communication template categories information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, getToken,paginate]);

  const getCommunicationTemplateCategoryByIdApi = useCallback(async (id, filters = {}) => {
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`communication-template-categories/${id}/edit`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let communicationTemplateCategories = {'data':[response.data.cTemplateCategory]}
      setCommunicationTemplateCategories(communicationTemplateCategories || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/communication-template-categories/edit/'+id))){
        if (getToken()) {
        
          
          await getCommunicationTemplateCategoryByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getCommunicationTemplateCategoryByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getCommunicationTemplateCategories(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getCommunicationTemplateCategories, searchParams, initialized,hideLoading,setApi]);

  const addCommunicationTemplateCategory = async (formData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call('communication-template-categories', 'POST', formData, true);
      
      if (response.status === 200) {
        // Refresh the communication template categories list
        await getCommunicationTemplateCategories();
        navigate(getRoutePath('/communication-template-categories'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to create communication template category'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create communication template category'
      });
      return { status: 500, error: error.message };
    }
  };

  const updateCommunicationTemplateCategory = async (id, formData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`communication-template-categories/${id}`, 'PUT', formData, true);
      
      if (response.status === 200) {
        // Refresh the communication template categories list
        await getCommunicationTemplateCategories();
        navigate(getRoutePath('/communication-template-categories'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to update communication template category'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update communication template category'
      });
      return { status: 500, error: error.message };
    }
  };

  const getCommunicationTemplateCategoryById = useCallback(async (id) => {
   // let category = communicationTemplateCategories?.data?.find(communicationTemplateCategory => Number(communicationTemplateCategory.id) === Number(id));

    //return {'status':200,'data':category};
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`communication-template-categories/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.cTemplateCategory };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch communication template category details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch communication template category details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const deleteCommunicationTemplateCategory = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`communication-template-categories/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the communication template categories list
        await getCommunicationTemplateCategories();
        navigate(getRoutePath('/communication-template-categories'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete communication template category'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete communication template category'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <CommunicationTemplateCategoryContext.Provider
      value={{
        communicationTemplateCategories,
        addCommunicationTemplateCategory,
        getCommunicationTemplateCategoryById,
        updateCommunicationTemplateCategory,
        deleteCommunicationTemplateCategory,
        getCommunicationTemplateCategories,
        formErrors,
        clearErrors,
        setPaginate,
        paginate
      }}
    >
      {children}
    </CommunicationTemplateCategoryContext.Provider>
  );
}; 